// src/components/profile/LevelExplorer.jsx
import React, { useState } from 'react';
import { ChevronDown, ChevronUp, X } from 'lucide-react';

const LEVEL_DATA = {
  A1: {
    emoji:       '🌱',
    name:        'Principiante',
    color:       'from-green-400 to-emerald-500',
    badge:       'bg-emerald-100 text-emerald-700',
    description: 'Puedes presentarte y usar expresiones básicas del día a día. Entiendes palabras familiares y frases muy sencillas.',
    canDo: [
      'Presentarte y preguntar el nombre de alguien',
      'Entender preguntas simples sobre ti mismo',
      'Hablar de dónde eres y de personas que conoces',
      'Interactuar de forma básica si el otro habla despacio',
    ],
    nextStep: 'Módulo 1 — Principiante',
  },
  A2: {
    emoji:       '🌿',
    name:        'Elemental',
    color:       'from-teal-400 to-cyan-500',
    badge:       'bg-cyan-100 text-cyan-700',
    description: 'Puedes comunicarte en situaciones cotidianas y familiares. Describes aspectos de tu entorno con frases simples.',
    canDo: [
      'Hablar de tu trabajo, familia y rutina diaria',
      'Hacer compras, pedir direcciones y pedir comida',
      'Entender textos cortos y sencillos',
      'Escribir mensajes simples como postales o notas',
    ],
    nextStep: 'Módulo 3 — Elemental',
  },
  B1: {
    emoji:       '🪴',
    name:        'Intermedio',
    color:       'from-blue-400 to-indigo-500',
    badge:       'bg-indigo-100 text-indigo-700',
    description: 'Puedes desenvolverte en la mayoría de situaciones de viaje. Produces texto sencillo sobre temas familiares o de interés personal.',
    canDo: [
      'Entender los puntos principales de temas conocidos',
      'Describir experiencias, eventos y aspiraciones',
      'Explicar brevemente razones y opiniones',
      'Seguir conversaciones en inglés con hablantes nativos',
    ],
    nextStep: 'Módulo 6 — Intermedio',
  },
  B2: {
    emoji:       '🌳',
    name:        'Intermedio Alto',
    color:       'from-violet-400 to-purple-500',
    badge:       'bg-purple-100 text-purple-700',
    description: 'Puedes entender las ideas principales de textos complejos, incluidos temas técnicos en tu área. Te comunicas con fluidez y espontaneidad.',
    canDo: [
      'Interactuar con fluidez con hablantes nativos',
      'Producir textos claros y detallados sobre muchos temas',
      'Explicar puntos de vista y sus ventajas y desventajas',
      'Entender programas de TV, películas y noticias en inglés',
    ],
    nextStep: 'Módulo 9 — Intermedio Alto',
  },
  C1: {
    emoji:       '🌲',
    name:        'Avanzado',
    color:       'from-orange-400 to-rose-500',
    badge:       'bg-rose-100 text-rose-700',
    description: 'Puedes usar el inglés de manera flexible y eficaz en contextos sociales, académicos y profesionales con alta precisión.',
    canDo: [
      'Entender textos largos y complejos con matices implícitos',
      'Expresarte de forma fluida sin buscar las palabras',
      'Usar el idioma eficazmente para fines sociales y profesionales',
      'Producir textos bien estructurados sobre temas complejos',
    ],
    nextStep: 'Módulo 10 — Avanzado',
  },
  C2: {
    emoji:       '🌍',
    name:        'Maestría',
    color:       'from-yellow-400 to-amber-500',
    badge:       'bg-amber-100 text-amber-700',
    description: 'Dominas el inglés con la precisión de un hablante nativo culto. Entiendes con facilidad prácticamente todo lo que escuchas o lees.',
    canDo: [
      'Resumir información de fuentes orales y escritas complejas',
      'Reconstruir argumentos en una presentación coherente',
      'Expresarte de forma espontánea, fluida y precisa',
      'Distinguir matices sutiles de significado en contextos complejos',
    ],
    nextStep: 'Vocabulario Master — Maestría',
  },
};

const LEVELS_ORDER = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'];

export default function LevelExplorer({ userLevel }) {
  const [isOpen,       setIsOpen]       = useState(false);
  const [activeLevel,  setActiveLevel]  = useState(userLevel || 'A1');

  const data = LEVEL_DATA[activeLevel];

  return (
    <div className="w-full">
      {/* ── Botón principal ─────────────────────────── */}
      <button
        onClick={() => setIsOpen(prev => !prev)}
        className="w-full flex items-center justify-between px-4 py-3 bg-white border-2 border-indigo-100 rounded-2xl shadow-sm hover:border-indigo-300 transition-all"
      >
        <div className="flex items-center gap-3">
          <span className="text-xl">{LEVEL_DATA[userLevel || 'A1']?.emoji}</span>
          <div className="text-left">
            <p className="text-[10px] font-black text-indigo-400 uppercase tracking-widest">
              Mi nivel
            </p>
            <p className="text-sm font-black text-slate-700">
              {userLevel} — {LEVEL_DATA[userLevel || 'A1']?.name}
            </p>
          </div>
        </div>
        {isOpen
          ? <ChevronUp  size={18} className="text-indigo-400" />
          : <ChevronDown size={18} className="text-indigo-400" />
        }
      </button>

      {/* ── Panel expandido ──────────────────────────── */}
      {isOpen && (
        <div className="mt-2 bg-white rounded-3xl border-2 border-indigo-50 shadow-xl overflow-hidden">

          {/* Selector de niveles */}
          <div className="flex border-b border-slate-100">
            {LEVELS_ORDER.map(lvl => (
              <button
                key={lvl}
                onClick={() => setActiveLevel(lvl)}
                className={`flex-1 py-3 text-xs font-black transition-all relative
                  ${activeLevel === lvl
                    ? 'text-indigo-600 bg-indigo-50'
                    : 'text-slate-400 hover:text-slate-600 hover:bg-slate-50'
                  }`}
              >
                {lvl}
                {/* Indicador del nivel del usuario */}
                {lvl === userLevel && (
                  <span className="absolute top-1 right-1 w-1.5 h-1.5 bg-indigo-500 rounded-full" />
                )}
              </button>
            ))}
          </div>

          {/* Contenido del nivel activo */}
          <div className="p-5 space-y-4">

            {/* Header */}
            <div className="flex items-center gap-3">
              <div className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${data.color} flex items-center justify-center text-2xl shadow-md`}>
                {data.emoji}
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <span className={`text-xs font-black px-2 py-0.5 rounded-full ${data.badge}`}>
                    {activeLevel}
                  </span>
                  {activeLevel === userLevel && (
                    <span className="text-[10px] font-black text-indigo-500 bg-indigo-50 px-2 py-0.5 rounded-full">
                      ← tu nivel
                    </span>
                  )}
                </div>
                <p className="text-base font-black text-slate-800 mt-0.5">{data.name}</p>
              </div>
            </div>

            {/* Descripción */}
            <p className="text-sm text-slate-600 leading-relaxed">
              {data.description}
            </p>

            {/* Can-do list */}
            <div>
              <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">
                En este nivel puedes...
              </p>
              <ul className="space-y-2">
                {data.canDo.map((item, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                    <span className="text-emerald-500 font-black mt-0.5">✓</span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* Siguiente paso */}
            <div className="bg-indigo-50 rounded-2xl px-4 py-3 flex items-center justify-between">
              <div>
                <p className="text-[10px] font-black text-indigo-400 uppercase tracking-widest">
                  Módulo recomendado
                </p>
                <p className="text-sm font-black text-indigo-700 mt-0.5">
                  {data.nextStep}
                </p>
              </div>
              <span className="text-lg">📚</span>
            </div>

          </div>
        </div>
      )}
    </div>
  );
}
